// Selector de idioma (i18n). Dropdown accesible con el idioma activo y la lista
// de idiomas disponibles. Funciona tanto en la capa pública (header de
// marketing, usuarios no autenticados) como en la app interna (menú/cuenta).
//
// Al elegir un idioma, `useLanguage().setLocale` cambia la UI al instante y
// persiste la preferencia (en el backend si hay sesión, para que prevalezca en
// el próximo login; siempre en localStorage como respaldo).

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../i18n/LanguageProvider.js';
import { localeInfo } from '../i18n/locales.js';

interface Props {
  /** Variante visual: `header` (compacta) o `block` (ancho completo). */
  variant?: 'header' | 'block';
  /** Clases extra para el contenedor. */
  className?: string;
}

export function LanguageSelector({ variant = 'header', className = '' }: Props) {
  const { t } = useTranslation();
  const { locale, available, setLocale } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Cierra al hacer clic fuera o con Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = localeInfo(locale);
  const triggerBase =
    variant === 'block'
      ? 'w-full justify-between field-slot'
      : 'h-10 px-space-sm rounded-lg text-on-surface-variant hover:text-on-surface';

  return (
    <div ref={ref} className={`relative ${variant === 'block' ? 'w-full' : ''} ${className}`}>
      <button
        type="button"
        className={`inline-flex items-center gap-space-xs font-body-sm text-body-sm transition-colors ${triggerBase}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.select')}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="material-symbols-outlined text-[18px]">language</span>
        <span>{active.nativeName}</span>
        <span className="material-symbols-outlined text-[18px]">{open ? 'expand_less' : 'expand_more'}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('language.label')}
          className={`absolute z-50 mt-1 max-h-72 overflow-auto rounded-lg border border-border-subtle bg-surface-deck shadow-xl py-1 ${
            variant === 'block' ? 'left-0 right-0' : 'right-0 min-w-[11rem]'
          }`}
        >
          {available.map((l) => {
            const isActive = l.code === locale;
            return (
              <li key={l.code} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => {
                    setLocale(l.code);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-space-sm px-space-md py-2 text-left font-body-sm text-body-sm transition-colors hover:bg-surface-rack ${
                    isActive ? 'text-primary' : 'text-on-surface'
                  }`}
                  lang={l.tag}
                >
                  <span>{l.nativeName}</span>
                  {isActive && <span className="material-symbols-outlined text-[18px]">check</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
